
import React, { useMemo } from 'react';
import { Marker, Polyline, Polygon, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import type { DrawingToolType } from '../../hooks/useDrawingMode';

import { BRAND } from '../../constants/mapColors';
interface DrawingEditLayerProps {
  points: [number, number][];
  toolType: DrawingToolType;
  onUpdatePoint: (index: number, lat: number, lon: number) => void;
  onInsertPoint: (afterIndex: number, lat: number, lon: number) => void;
  onRemovePoint: (index: number) => void;
}

const vertexIcon = L.divIcon({
  html: `<div style="width:14px;height:14px;border-radius:50%;background:${BRAND};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3)"></div>`,
  className: '',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const midpointIcon = L.divIcon({
  html: `<div style="width:10px;height:10px;border-radius:50%;background:${BRAND};opacity:0.4;border:2px solid white"></div>`,
  className: '',
  iconSize: [10, 10],
  iconAnchor: [5, 5],
});

const DrawingEditLayer: React.FC<DrawingEditLayerProps> = ({
  points, toolType, onUpdatePoint, onInsertPoint, onRemovePoint,
}) => {
  const positions = points.map(([lat, lon]) => [lat, lon] as [number, number]);

  // Midpoints between vertices
  const midpoints = useMemo(() => {
    const mps: { lat: number; lon: number; afterIndex: number }[] = [];
    for (let i = 0; i < points.length - 1; i++) {
      mps.push({
        lat: (points[i][0] + points[i + 1][0]) / 2,
        lon: (points[i][1] + points[i + 1][1]) / 2,
        afterIndex: i,
      });
    }
    // Close polygon midpoint
    if (toolType === 'area' && points.length >= 3) {
      const last = points.length - 1;
      mps.push({
        lat: (points[last][0] + points[0][0]) / 2,
        lon: (points[last][1] + points[0][1]) / 2,
        afterIndex: last,
      });
    }
    return mps;
  }, [points, toolType]);

  return (
    <>
      {/* Shape outline */}
      {points.length >= 2 && toolType === 'line' && (
        <Polyline
          positions={positions}
          pathOptions={{ color: BRAND, weight: 3, dashArray: '6,4', opacity: 0.7 }}
        />
      )}
      {points.length >= 3 && toolType === 'area' && (
        <Polygon
          positions={positions}
          pathOptions={{ color: BRAND, weight: 3, dashArray: '6,4', fillColor: BRAND, fillOpacity: 0.08 }}
        />
      )}

      {/* Midpoint markers (insert new vertex) */}
      {midpoints.map((mp, i) => (
        <Marker
          key={`mid-${i}`}
          position={[mp.lat, mp.lon]}
          icon={midpointIcon}
          eventHandlers={{
            click: () => onInsertPoint(mp.afterIndex, mp.lat, mp.lon),
          }}
        />
      ))}

      {/* Vertex markers (draggable) */}
      {points.map(([lat, lon], i) => (
        <Marker
          key={`v-${i}`}
          position={[lat, lon]}
          icon={vertexIcon}
          draggable
          eventHandlers={{
            dragend: (e: any) => {
              const latlng = e.target.getLatLng();
              onUpdatePoint(i, latlng.lat, latlng.lng);
            },
          }}
        />
      ))}
    </>
  );
};

export default DrawingEditLayer;
